// src/app/pages/procedimientos/audioTrigger/keyboard-trigger.service.ts

import { Injectable, EventEmitter, NgZone } from '@angular/core';

/**
 * Configuración del Keyboard Trigger
 */
export interface KeyboardTriggerConfig {
  tiempoEspera?: number; // Milisegundos entre disparos (por defecto: 400ms)
  teclas?: string[]; // Teclas personalizadas (opcional)
}

/**
 * Interface del servicio de Keyboard Trigger
 */
export interface IKeyboardTriggerService {
  onTeclaDetectada: EventEmitter<void>;
  iniciar(config?: KeyboardTriggerConfig): Promise<boolean>;
  detener(): void;
  ajustarTiempoEspera(ms: number): void;
  isActive(): boolean;
}

/**
 * Servicio de Keyboard Trigger para detectar teclas de presentadores
 * Compatible con Logitech, Kensington, Targus y genéricos
 *
 * Teclas detectadas por defecto:
 * - PageDown, PageUp (Logitech R400/R500, Targus)
 * - ArrowRight, ArrowLeft (Kensington)
 * - Space, Enter (Genéricos)
 * - Period/. (Logitech laser pointer)
 * - F5 (Modo presentación)
 */
@Injectable({
  providedIn: 'root',
})
export class KeyboardTriggerService implements IKeyboardTriggerService {
  // EventEmitter para notificar cuando se detecta una tecla
  public onTeclaDetectada = new EventEmitter<void>();

  // Estado del servicio
  private detectando = false;

  // Configuración
  private tiempoEsperaMs = 400;

  // Control de cooldown
  private ultimaDeteccion = 0;
  private cooldownActivo = false;

  // Teclas de presentadores comunes
  private readonly TECLAS_PRESENTADOR: string[] = [
    'PageDown', // Logitech R400/R500, Targus (botón adelante)
    'PageUp', // Logitech R400/R500, Targus (botón atrás)
    'ArrowRight', // Kensington (adelante)
    'ArrowLeft', // Kensington (atrás)
    'Space', // Genéricos
    'Enter', // Genéricos
    '.', // Logitech (laser pointer)
    'Period', // Logitech (laser pointer - nombre alternativo)
    'F5', // Modo presentación
  ];

  // Teclas configuradas (se puede personalizar)
  private teclasActivas: string[] = [];

  // Listener reference para poder removerlo
  private keydownListener: ((event: KeyboardEvent) => void) | null = null;

  constructor(private zone: NgZone) {
    console.log('🔧 KeyboardTriggerService inicializado');
  }

  /**
   * Inicia la detección de teclas de presentador
   */
  async iniciar(config?: KeyboardTriggerConfig): Promise<boolean> {
    try {
      // Aplicar configuración
      if (config) {
        if (config.tiempoEspera !== undefined) {
          this.tiempoEsperaMs = config.tiempoEspera;
        }
        if (config.teclas && config.teclas.length > 0) {
          this.teclasActivas = config.teclas;
        } else {
          this.teclasActivas = [...this.TECLAS_PRESENTADOR];
        }
      } else {
        this.teclasActivas = [...this.TECLAS_PRESENTADOR];
      }

      console.log('⌨️ Iniciando Keyboard Trigger...');
      console.log(`   Cooldown: ${this.tiempoEsperaMs}ms`);
      console.log(`   Teclas detectadas: ${this.teclasActivas.join(', ')}`);

      // Crear listener
      this.keydownListener = (event: KeyboardEvent) => {
        this.handleKeyPress(event);
      };

      // Agregar listener global
      window.addEventListener('keydown', this.keydownListener);

      this.detectando = true;

      console.log('✅ Keyboard Trigger activado exitosamente');
      console.log('💡 Presiona cualquier botón del presentador para tomar foto');

      return true;
    } catch (error) {
      console.error('❌ Error iniciando Keyboard Trigger:', error);
      return false;
    }
  }

  /**
   * Maneja las teclas presionadas
   */
  private handleKeyPress(event: KeyboardEvent): void {
    // Verificar si es una tecla de presentador
    if (!this.teclasActivas.includes(event.key)) {
      return; // No es una tecla de presentador, ignorar
    }

    // Verificar cooldown
    const ahora = Date.now();
    if (this.cooldownActivo) {
      const falta = this.tiempoEsperaMs - (ahora - this.ultimaDeteccion);
      console.log(`⏳ Tecla en cooldown (${falta}ms restantes)`);
      return;
    }

    // Prevenir acción por defecto (evitar scroll, etc.)
    event.preventDefault();
    event.stopPropagation();

    // Log de tecla detectada
    console.log(`⌨️⌨️⌨️ ¡TECLA DETECTADA! ⌨️⌨️⌨️`);
    console.log(`   Tecla: "${event.key}"`);
    console.log(`   Código: ${event.code}`);

    // Activar cooldown
    this.cooldownActivo = true;
    this.ultimaDeteccion = ahora;

    // Emitir evento (dentro de NgZone para que Angular detecte el cambio)
    this.zone.run(() => {
      this.onTeclaDetectada.emit();
    });

    // Desactivar cooldown después del tiempo configurado
    setTimeout(() => {
      this.cooldownActivo = false;
      console.log('✅ Cooldown terminado - Listo para nueva foto');
    }, this.tiempoEsperaMs);
  }

  /**
   * Detiene la detección de teclas
   */
  detener(): void {
    console.log('🛑 Deteniendo Keyboard Trigger...');

    this.detectando = false;

    // Remover listener
    if (this.keydownListener) {
      window.removeEventListener('keydown', this.keydownListener);
      this.keydownListener = null;
    }

    this.cooldownActivo = false;
    this.ultimaDeteccion = 0;

    console.log('✅ Keyboard Trigger detenido');
  }

  /**
   * Ajusta el tiempo de espera entre disparos en tiempo real
   */
  ajustarTiempoEspera(ms: number): void {
    if (ms < 100) {
      console.warn('⚠️ Tiempo de espera muy corto, mínimo 100ms');
      this.tiempoEsperaMs = 100;
    } else if (ms > 5000) {
      console.warn('⚠️ Tiempo de espera muy largo, máximo 5000ms');
      this.tiempoEsperaMs = 5000;
    } else {
      this.tiempoEsperaMs = ms;
      console.log(`✅ Tiempo de espera ajustado: ${ms}ms`);
    }
  }

  /**
   * Verifica si el servicio está activo
   */
  isActive(): boolean {
    return this.detectando;
  }

  /**
   * Obtiene la lista de teclas actualmente detectadas
   */
  getTeclasActivas(): string[] {
    return [...this.teclasActivas];
  }

  /**
   * Permite agregar teclas personalizadas en tiempo real
   */
  agregarTecla(tecla: string): void {
    if (!this.teclasActivas.includes(tecla)) {
      this.teclasActivas.push(tecla);
      console.log(`✅ Tecla agregada: "${tecla}"`);
      console.log(`   Teclas activas: ${this.teclasActivas.join(', ')}`);
    }
  }

  /**
   * Permite remover teclas en tiempo real
   */
  removerTecla(tecla: string): void {
    const index = this.teclasActivas.indexOf(tecla);
    if (index > -1) {
      this.teclasActivas.splice(index, 1);
      console.log(`✅ Tecla removida: "${tecla}"`);
      console.log(`   Teclas activas: ${this.teclasActivas.join(', ')}`);
    }
  }
}
